/**
 * Get H2 Heading blocks array
 * @param {blocks Array} blocks 
 * @returns H2 Headings Block Array
 */
export const getH2Headings = (blocks) => blocks.filter((block) => (block.name === "core/heading") && !block.attributes?.level);

// Must mirror the id CoreBlocks/Heading renders, otherwise the anchors and the
// ItemList urls point at fragments that don't exist in the document.
export const getHeadingAnchorId = (heading, generateHeadingID) => {
  if(heading?.attributes?.jltId) return heading?.attributes?.jltId;

  // Generated ID and title is available then dynamic created
  if(generateHeadingID && heading?.attributes?.page_content) {
    return generateSeoId(heading.attributes.page_content);
  }

  return '';
}

/**
 * Generate SEO-friendly HTML ID from title text
 * @param {string} title - The title text to convert
 * @param {Object} options - Configuration options
 * @returns {string} - SEO-friendly ID
 */
function generateSeoId(title, options = {}) {
    const {
        separator = '-',           // Word separator
        maxLength = 60,           // Max ID length (SEO best practice)
        lowercase = true,         // Convert to lowercase
        removeStopWords = false,  // Remove common stop words
        prefix = ''               // Optional prefix
    } = options;

    // Common stop words (optional removal for shorter IDs)
    const stopWords = ['a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 
                       'to', 'for', 'of', 'with', 'by', 'via', 'from'];

    let id = title
        // Normalize unicode characters (é → e, ñ → n, etc.)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        
        // Replace special characters and symbols with separator
        .replace(/[^\w\s-]/g, separator)
        
        // Replace multiple spaces/separators with single separator
        .replace(/[\s_-]+/g, separator)
        
        // Remove leading/trailing separators
        .replace(new RegExp(`^${separator}+|${separator}+$`, 'g'), '');

    // Convert to lowercase
    if (lowercase) {
        id = id.toLowerCase();
    }

    // Remove stop words if requested
    if (removeStopWords) {
        id = id
            .split(separator)
            .filter(word => !stopWords.includes(word.toLowerCase()))
            .join(separator);
    }

    // Trim to max length (at word boundary)
    if (id.length > maxLength) {
        id = id.substring(0, maxLength);
        const lastSep = id.lastIndexOf(separator);
        if (lastSep > maxLength * 0.6) {
            id = id.substring(0, lastSep);
        }
    }

    // Add prefix if provided
    if (prefix) {
        id = `${prefix}${separator}${id}`;
    }

    return id;
}


// Heading content is WordPress HTML; schema.org `name` has to be plain text.
export const toPlainText = (html = "") =>
  String(html)
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();


/**
 * 
 * @param { Array } headings 
 * @param {page URL} url 
 * @returns JSON_LD
 */
export function getTableOfContentJsonLd(headings, url) {
  const itemListElement = headings
    .map((heading) => {
      const name = toPlainText(heading?.attributes?.page_content);
      if (!name) return null;

      return {
        "@type": "ListItem",
        name,
        ...(url ? { url: `${url}#${getHeadingAnchorId(heading, true)}` } : {}),
      };
    })
    .filter(Boolean)
    // position after filtering, so dropped empty headings leave no gaps
    .map((item, i) => ({ ...item, position: i + 1 }));

  if (!itemListElement.length) return null;

  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Table of Contents",
    itemListElement,
  }).replace(/</g, "\\u003c");
}